import type { MermaidConfig } from "mermaid";
import type { MermaidSettings } from "./mermaid-settings";

export interface EditorDiagramPalette {
  background: string;
  surface: string;
  text: string;
  accent: string;
  border: string;
  fontFamily: string;
  dark: boolean;
}

export function readEditorDiagramPalette(): EditorDiagramPalette {
  const style = getComputedStyle(document.documentElement);
  const color = (name: string, fallback: string): string => {
    const value = style.getPropertyValue(name).trim();
    return /^#[\da-f]{3,8}$/i.test(value) ? value : fallback;
  };
  return {
    background: color("--bg-editor", "#ffffff"),
    surface: color("--bg-surface", "#f4f4f4"),
    text: color("--text-editor", "#222222"),
    accent: color("--text-link", "#3366cc"),
    border: color("--border-divider", "#888888"),
    fontFamily: style.getPropertyValue("--font-family").trim() || "monospace",
    dark: style.getPropertyValue("--color-scheme").trim() === "dark",
  };
}

export function mermaidConfig(
  settings: MermaidSettings,
  palette: EditorDiagramPalette,
): MermaidConfig {
  const base: MermaidConfig = {
    startOnLoad: false,
    securityLevel: "strict",
    suppressErrorRendering: true,
    htmlLabels: false,
    maxTextSize: 20_000,
    maxEdges: 300,
    look: "classic",
    layout: "dagre",
    fontFamily: palette.fontFamily,
  };
  if (settings.theme !== "editor") return { ...base, theme: settings.theme };
  return {
    ...base,
    theme: "base",
    themeVariables: {
      darkMode: palette.dark,
      background: palette.background,
      primaryColor: palette.surface,
      primaryTextColor: palette.text,
      primaryBorderColor: palette.border,
      secondaryColor: palette.background,
      secondaryTextColor: palette.text,
      secondaryBorderColor: palette.border,
      tertiaryColor: palette.surface,
      tertiaryTextColor: palette.text,
      tertiaryBorderColor: palette.border,
      lineColor: palette.accent,
      textColor: palette.text,
      mainBkg: palette.surface,
      nodeBorder: palette.border,
      clusterBkg: palette.background,
      clusterBorder: palette.border,
      edgeLabelBackground: palette.background,
      fontFamily: palette.fontFamily,
    },
  };
}

let renderQueue: Promise<unknown> = Promise.resolve();

export function renderMermaidSvg(
  code: string,
  settings: MermaidSettings,
  palette: EditorDiagramPalette,
): Promise<string> {
  const task = renderQueue.then(async () => {
    const mermaid = (await import("mermaid")).default;
    mermaid.initialize(mermaidConfig(settings, palette));
    await mermaid.parse(code);
    const id = `turbonotes-mermaid-${crypto.randomUUID().replace(/-/g, "")}`;
    return (await mermaid.render(id, code)).svg;
  });
  renderQueue = task.catch(() => {});
  return task;
}
